<script>
  import { wheelStore } from '$lib/store/wheelStore';
  import { storageUtils } from '$lib/utils/storage';
  import { onMount, createEventDispatcher } from 'svelte';
  
  let newName = '';
  let bulkEntries = '';
  let showBulkInput = false;
  
  const dispatch = createEventDispatcher();
  
  onMount(() => {
    // Update the UI to reflect any names loaded in the wheel store
    if ($wheelStore.names.length > 0) {
      saveToLocalStorage();
    }
  });
  
  function addName() {
    if (newName.trim()) {
      wheelStore.addName(newName.trim());
      newName = '';
      saveToLocalStorage();
      dispatch('namesChanged');
    }
  }
  
  function removeName(index) {
    wheelStore.removeName(index);
    saveToLocalStorage();
    dispatch('namesChanged');
  }
  
  function processKeydown(event) {
    if (event.key === 'Enter') {
      addName();
    }
  }
  
  function addBulkNames() {
    if (!bulkEntries.trim()) return;
    
    const names = bulkEntries
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);
      
    wheelStore.addNames(names);
    bulkEntries = '';
    showBulkInput = false;
    saveToLocalStorage();
    dispatch('namesChanged');
  }
  
  function clearAllNames() {
    if (confirm('Are you sure you want to clear all names?')) {
      wheelStore.clearNames();
      saveToLocalStorage();
      dispatch('namesChanged');
    }
  }
  
  function saveToLocalStorage() {
    storageUtils.saveNames($wheelStore.names);
  }
</script>

<div class="name-input-container">
  <h2>Entries</h2>
  
  <div class="input-group">
    <input
      type="text"
      bind:value={newName}
      placeholder="Add a name or option"
      on:keydown={processKeydown}
    />
    <button on:click={addName}>Add</button>
  </div>
  
  <div class="options">
    <button class="text-button" on:click={() => showBulkInput = !showBulkInput}>
      {showBulkInput ? 'Cancel Bulk Add' : 'Bulk Add Names'}
    </button>
    <button class="text-button danger" on:click={clearAllNames}>
      Clear All
    </button>
  </div>
  
  {#if showBulkInput}
    <div class="bulk-input">
      <textarea
        bind:value={bulkEntries}
        placeholder="Add multiple names, one per line"
        rows="5"
      ></textarea>
      <button on:click={addBulkNames}>Add All Names</button>
    </div>
  {/if}
  
  <div class="names-list">
    <h3>Current Entries: {$wheelStore.names.length}</h3>
    
    {#if $wheelStore.names.length > 0}
      <ul>
        {#each $wheelStore.names as name, i}
          <li>
            <span>{name}</span>
            <button class="remove-button" on:click={() => removeName(i)}>×</button>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="empty-message">No names added yet. Add some names to spin the wheel!</p>
    {/if}
  </div>
</div>

<style>
  .name-input-container {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  }
  
  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
  }
  
  .input-group {
    display: flex;
    margin-bottom: 1rem;
  }
  
  input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px 0 0 4px;
    font-size: 1rem;
  }
  
  .input-group button {
    padding: 0.5rem 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 0 4px 4px 0;
    cursor: pointer;
  }
  
  .options {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  
  .text-button {
    background: none;
    border: none;
    color: #007bff;
    cursor: pointer;
    padding: 0;
    font-size: 0.875rem;
  }
  
  .text-button:hover {
    text-decoration: underline;
  }
  
  .text-button.danger {
    color: #dc3545;
  }
  
  .bulk-input {
    margin-bottom: 1rem;
  }
  
  textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }
  
  .bulk-input button {
    padding: 0.5rem 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
  }
  
  .names-list {
    max-height: 300px;
    overflow-y: auto;
  }
  
  h3 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
    border-bottom: 1px solid #eee;
  }
  
  .remove-button {
    background: none;
    border: none;
    color: #dc3545;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0 0.5rem;
  }
  
  .empty-message {
    color: #6c757d;
    font-style: italic;
  }
  
  @media (max-width: 767px) {
    .name-input-container {
      padding: 1rem;
    }
    
    h2 {
      font-size: 1.3rem;
      margin-bottom: 0.75rem;
    }
    
    .input-group {
      margin-bottom: 0.75rem;
    }
    
    input, .input-group button {
      padding: 0.4rem 0.8rem;
      font-size: 0.9rem;
    }
    
    textarea {
      padding: 0.4rem;
      font-size: 0.9rem;
      rows: 4;
    }
    
    .bulk-input button {
      padding: 0.4rem 0.8rem;
    }
    
    .names-list {
      max-height: 200px;
    }
    
    h3 {
      font-size: 0.9rem;
    }
    
    li {
      padding: 0.4rem;
      font-size: 0.9rem;
    }
  }
</style>